import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';

import { CHANNEL_LABELS } from './HwcgEocActionPanel';
import {
  HWCG_ACTIVATION_MESSAGE_KEY,
  HWCG_EOC_CHANNELS,
  type HwcgActivationMessages,
  type HwcgEocChannel,
} from './hwcgEocNotice';

const MESSAGE_MAX = 4000;
const SUBJECT_MAX = 255;
const SMS_SEGMENT = 160;

/**
 * One activation request, worded per channel. Each enabled channel starts on
 * the default script; editing a channel stores its own text, and clearing it
 * back to the default drops the override.
 */
export function ActivationChannelMessages({
  idPrefix,
  channels,
  onChannelsChange,
  messages,
  onMessagesChange,
  defaultMessage,
  defaultSubject,
}: {
  idPrefix: string;
  channels: HwcgEocChannel[];
  onChannelsChange: (next: HwcgEocChannel[]) => void;
  messages: Partial<HwcgActivationMessages>;
  onMessagesChange: (next: Partial<HwcgActivationMessages>) => void;
  defaultMessage: string;
  defaultSubject: string;
}) {
  const [picked, setPicked] = useState<HwcgEocChannel>(channels[0] ?? 'inApp');
  const active = channels.includes(picked) ? picked : channels[0];

  const setMessage = (
    key: keyof HwcgActivationMessages,
    value: string,
    fallback: string
  ) => {
    const next = { ...messages };
    if (value.trim() === '' || value === fallback) delete next[key];
    else next[key] = value;
    onMessagesChange(next);
  };

  const toggleChannel = (channel: HwcgEocChannel, on: boolean) => {
    const next = on
      ? HWCG_EOC_CHANNELS.filter(
          (current) => current === channel || channels.includes(current)
        )
      : channels.filter((current) => current !== channel);
    onChannelsChange(next);
    if (on) setPicked(channel);
  };

  const key = active ? HWCG_ACTIVATION_MESSAGE_KEY[active] : null;
  const body = key ? (messages[key] ?? defaultMessage) : '';

  return (
    <div className="tw:grid tw:gap-2">
      <div className="tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
        Activation request
      </div>
      <div className="tw:flex tw:flex-wrap tw:gap-3">
        {HWCG_EOC_CHANNELS.map((channel) => (
          <label
            key={channel}
            className="tw:flex tw:items-center tw:gap-2 tw:text-sm"
          >
            <Checkbox
              checked={channels.includes(channel)}
              onCheckedChange={(value) =>
                toggleChannel(channel, value === true)
              }
            />
            {CHANNEL_LABELS[channel]}
          </label>
        ))}
      </div>
      {active && key ? (
        <div className="tw:grid tw:gap-2 tw:rounded-md tw:border tw:border-border tw:p-2">
          <Tabs
            value={active}
            onValueChange={(value) => setPicked(value as HwcgEocChannel)}
          >
            <TabsList>
              {channels.map((channel) => (
                <TabsTrigger key={channel} value={channel}>
                  {CHANNEL_LABELS[channel]}
                  {messages[HWCG_ACTIVATION_MESSAGE_KEY[channel]] ? (
                    <span
                      className="tw:size-1.5 tw:rounded-full tw:bg-primary"
                      aria-label="Edited"
                    />
                  ) : null}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {active === 'email' ? (
            <div className="tw:grid tw:gap-1.5">
              <Label htmlFor={`${idPrefix}-activation-email-subject`}>
                Email subject
              </Label>
              <Input
                id={`${idPrefix}-activation-email-subject`}
                maxLength={SUBJECT_MAX}
                value={messages.emailSubject ?? ''}
                placeholder={defaultSubject}
                onChange={(event) =>
                  setMessage('emailSubject', event.target.value, '')
                }
              />
            </div>
          ) : null}
          <div className="tw:grid tw:gap-1.5">
            <Label htmlFor={`${idPrefix}-activation-${active}`}>
              {active === 'voice'
                ? 'Call script'
                : `${CHANNEL_LABELS[active]} message`}
            </Label>
            <Textarea
              id={`${idPrefix}-activation-${active}`}
              className="tw:min-h-28"
              maxLength={MESSAGE_MAX}
              value={body}
              onChange={(event) =>
                setMessage(key, event.target.value, defaultMessage)
              }
            />
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:text-xs tw:text-muted-foreground">
              <span>
                {active === 'sms'
                  ? `${Math.max(1, Math.ceil(body.length / SMS_SEGMENT))} text message(s). Replies: 1 reported, 9 cannot report.`
                  : 'Replies: 1 reported, 9 cannot report.'}
              </span>
              <div className="tw:flex tw:items-center tw:gap-2">
                {messages[key] ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    onClick={() => setMessage(key, '', defaultMessage)}
                  >
                    Reset to default
                  </Button>
                ) : null}
                <span>
                  {body.length}/{MESSAGE_MAX}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="tw:m-0 tw:text-xs tw:text-destructive">
          Choose at least one channel.
        </p>
      )}
    </div>
  );
}
